"use client";
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';

/* ─────────────────────────────────────────
   Illustrations — large decorative SVGs
   that sit at the bottom of each card
───────────────────────────────────────── */

const IllustrationResearch = () => (
    <svg viewBox="0 0 260 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        {/* Wavy contour lines (background) */}
        <ellipse cx="130" cy="160" rx="110" ry="40" stroke="rgba(255,255,255,0.07)" strokeWidth="1.2"/>
        <ellipse cx="130" cy="160" rx="85" ry="30" stroke="rgba(255,255,255,0.07)" strokeWidth="1.2"/>
        <ellipse cx="130" cy="160" rx="60" ry="20" stroke="rgba(255,255,255,0.07)" strokeWidth="1.2"/>
        <ellipse cx="130" cy="160" rx="35" ry="12" stroke="rgba(255,255,255,0.07)" strokeWidth="1.2"/>
        {/* Clipboard / document */}
        <rect x="55" y="40" width="90" height="110" rx="6" fill="#2a2a2a" stroke="rgba(255,255,255,0.15)" strokeWidth="1.2"/>
        <rect x="80" y="32" width="40" height="14" rx="4" fill="#1a1a1a" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
        {/* Bar chart inside */}
        <rect x="68" y="100" width="10" height="32" rx="2" fill="#ceff1a" opacity="0.8"/>
        <rect x="84" y="88" width="10" height="44" rx="2" fill="#ceff1a" opacity="0.6"/>
        <rect x="100" y="108" width="10" height="24" rx="2" fill="#ceff1a" opacity="0.4"/>
        <rect x="116" y="92" width="10" height="40" rx="2" fill="#ceff1a" opacity="0.7"/>
        {/* Magnifying glass */}
        <circle cx="158" cy="90" r="28" fill="#1e1e1e" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5"/>
        <circle cx="155" cy="87" r="16" stroke="white" strokeWidth="2.5" fill="none"/>
        <line x1="166" y1="98" x2="178" y2="110" stroke="white" strokeWidth="3" strokeLinecap="round"/>
        {/* Plus lines on magnifier */}
        <line x1="155" y1="80" x2="155" y2="94" stroke="rgba(206,255,26,0.8)" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="148" y1="87" x2="162" y2="87" stroke="rgba(206,255,26,0.8)" strokeWidth="1.8" strokeLinecap="round"/>
        {/* Person icon */}
        <circle cx="60" cy="65" r="8" stroke="white" strokeWidth="1.5" fill="none"/>
        <path d="M48 85c0-6.6 5.4-12 12-12s12 5.4 12 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    </svg>
);

const IllustrationStrategy = () => (
    <svg viewBox="0 0 260 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        {/* Target / bullseye */}
        <circle cx="130" cy="120" r="70" stroke="rgba(255,255,255,0.06)" strokeWidth="1.2"/>
        <circle cx="130" cy="120" r="52" stroke="rgba(255,255,255,0.08)" strokeWidth="1.2"/>
        <circle cx="130" cy="120" r="34" stroke="rgba(255,255,255,0.1)" strokeWidth="1.2"/>
        <circle cx="130" cy="120" r="16" fill="rgba(206,255,26,0.15)" stroke="#ceff1a" strokeWidth="1.5"/>
        <circle cx="130" cy="120" r="5" fill="#ceff1a"/>
        {/* Rocket */}
        <g transform="translate(75, 45) rotate(-30)">
            <path d="M12 0C12 0 0 8 0 20L4 22L8 18L16 22L20 20C20 8 12 0 12 0Z" fill="#ceff1a" opacity="0.9"/>
            <path d="M4 22L0 30L8 28Z" fill="rgba(206,255,26,0.5)"/>
            <path d="M20 22L24 30L16 28Z" fill="rgba(206,255,26,0.5)"/>
            <circle cx="12" cy="14" r="4" fill="#1a1a1a"/>
            {/* Flame */}
            <path d="M8 30 Q12 40 16 30" fill="#ff8c42" opacity="0.8"/>
        </g>
        {/* Arrow hitting target */}
        <line x1="70" y1="70" x2="122" y2="118" stroke="white" strokeWidth="1.8" strokeDasharray="4 3"/>
        <polygon points="122,118 112,112 118,124" fill="white"/>
        {/* Map/strategy nodes */}
        <circle cx="40" cy="100" r="6" fill="#3730a3"/>
        <circle cx="40" cy="100" r="6" stroke="rgba(55,48,163,0.5)" strokeWidth="3"/>
        <circle cx="210" cy="80" r="6" fill="#3730a3"/>
        <line x1="46" y1="100" x2="204" y2="80" stroke="rgba(55,48,163,0.4)" strokeWidth="1" strokeDasharray="3 3"/>
    </svg>
);

const IllustrationUI = () => (
    <svg viewBox="0 0 260 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        {/* Browser frame */}
        <rect x="30" y="30" width="200" height="140" rx="10" fill="#1a1a1a" stroke="rgba(255,255,255,0.12)" strokeWidth="1.2"/>
        <rect x="30" y="30" width="200" height="26" rx="10" fill="#252525"/>
        <rect x="30" y="48" width="200" height="8" fill="#252525"/>
        {/* Traffic lights */}
        <circle cx="50" cy="43" r="5" fill="#ff5f57"/>
        <circle cx="64" cy="43" r="5" fill="#ffbd2e"/>
        <circle cx="78" cy="43" r="5" fill="#28c840"/>
        {/* URL bar */}
        <rect x="95" y="37" width="100" height="12" rx="4" fill="rgba(255,255,255,0.06)"/>
        {/* UI components inside */}
        {/* Hero block */}
        <rect x="44" y="66" width="172" height="34" rx="4" fill="rgba(206,255,26,0.08)" stroke="rgba(206,255,26,0.2)" strokeWidth="0.8"/>
        <rect x="54" y="74" width="50" height="6" rx="2" fill="#ceff1a" opacity="0.7"/>
        <rect x="54" y="84" width="80" height="4" rx="1.5" fill="rgba(255,255,255,0.2)"/>
        {/* Card grid */}
        <rect x="44" y="108" width="52" height="52" rx="4" fill="#252525" stroke="rgba(255,255,255,0.07)" strokeWidth="0.8"/>
        <rect x="104" y="108" width="52" height="52" rx="4" fill="#252525" stroke="rgba(255,255,255,0.07)" strokeWidth="0.8"/>
        <rect x="164" y="108" width="52" height="52" rx="4" fill="#252525" stroke="rgba(255,255,255,0.07)" strokeWidth="0.8"/>
        {/* Card content */}
        <rect x="52" y="116" width="36" height="4" rx="1" fill="rgba(206,255,26,0.5)"/>
        <rect x="52" y="124" width="28" height="3" rx="1" fill="rgba(255,255,255,0.15)"/>
        <rect x="52" y="130" width="32" height="3" rx="1" fill="rgba(255,255,255,0.1)"/>
        <rect x="112" y="116" width="36" height="4" rx="1" fill="rgba(55,48,163,0.8)"/>
        <rect x="112" y="124" width="28" height="3" rx="1" fill="rgba(255,255,255,0.15)"/>
        <rect x="172" y="116" width="36" height="4" rx="1" fill="rgba(206,255,26,0.5)"/>
        {/* Cursor */}
        <g transform="translate(148, 95)">
            <path d="M0 0L0 12L3.5 8.5L6 14L8 13L5.5 7L10 7Z" fill="white" stroke="#1a1a1a" strokeWidth="0.6"/>
        </g>
    </svg>
);

const IllustrationWireframe = () => (
    <svg viewBox="0 0 260 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        {/* Wavy lines background */}
        <ellipse cx="130" cy="170" rx="115" ry="35" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
        <ellipse cx="130" cy="170" rx="88" ry="26" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
        <ellipse cx="130" cy="170" rx="60" ry="18" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
        {/* Main wireframe sheet */}
        <rect x="40" y="20" width="140" height="160" rx="6" fill="#1e1e1e" stroke="rgba(255,255,255,0.15)" strokeWidth="1.2"/>
        {/* Wireframe nav */}
        <rect x="40" y="20" width="140" height="18" rx="6" fill="#2a2a2a"/>
        <rect x="40" y="30" width="140" height="8" fill="#2a2a2a"/>
        <rect x="50" y="25" width="18" height="6" rx="1" fill="rgba(255,255,255,0.2)"/>
        <rect x="110" y="25" width="10" height="6" rx="1" fill="rgba(255,255,255,0.1)"/>
        <rect x="126" y="25" width="10" height="6" rx="1" fill="rgba(255,255,255,0.1)"/>
        <rect x="142" y="25" width="10" height="6" rx="1" fill="rgba(255,255,255,0.1)"/>
        {/* Wireframe blocks */}
        <rect x="50" y="46" width="80" height="8" rx="2" fill="rgba(255,255,255,0.15)"/>
        <rect x="50" y="58" width="60" height="6" rx="2" fill="rgba(255,255,255,0.1)"/>
        <rect x="50" y="72" width="90" height="36" rx="3" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8"/>
        {/* Image placeholder icon */}
        <path d="M70 90 l8-12 6 8 5-5 10 13H70z" fill="rgba(255,255,255,0.12)"/>
        <circle cx="80" cy="82" r="3" fill="rgba(255,255,255,0.2)"/>
        <rect x="50" y="116" width="36" height="10" rx="3" fill="rgba(206,255,26,0.3)"/>
        <rect x="92" y="116" width="30" height="10" rx="3" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.12)" strokeWidth="0.7"/>
        {/* Annotation lines */}
        <line x1="154" y1="46" x2="185" y2="38" stroke="rgba(206,255,26,0.5)" strokeWidth="1" strokeDasharray="2 2"/>
        <circle cx="185" cy="38" r="3" fill="#ceff1a"/>
        <line x1="154" y1="90" x2="190" y2="90" stroke="rgba(206,255,26,0.5)" strokeWidth="1" strokeDasharray="2 2"/>
        <circle cx="190" cy="90" r="3" fill="#ceff1a"/>
        <line x1="154" y1="116" x2="185" y2="130" stroke="rgba(206,255,26,0.5)" strokeWidth="1" strokeDasharray="2 2"/>
        <circle cx="185" cy="130" r="3" fill="#ceff1a"/>
        {/* Figma-style handle */}
        <rect x="37" y="86" width="6" height="6" rx="1" fill="#ceff1a" opacity="0.8"/>
        <rect x="37" y="42" width="6" height="6" rx="1" fill="#ceff1a" opacity="0.8"/>
        <rect x="177" y="42" width="6" height="6" rx="1" fill="#ceff1a" opacity="0.8"/>
        <rect x="177" y="86" width="6" height="6" rx="1" fill="#ceff1a" opacity="0.8"/>
    </svg>
);

const IllustrationTesting = () => (
    <svg viewBox="0 0 260 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        {/* Large target */}
        <circle cx="140" cy="115" r="72" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
        <circle cx="140" cy="115" r="55" stroke="rgba(255,255,255,0.07)" strokeWidth="1.2"/>
        <circle cx="140" cy="115" r="38" stroke="rgba(255,255,255,0.09)" strokeWidth="1.2"/>
        <circle cx="140" cy="115" r="21" stroke="rgba(206,255,26,0.2)" strokeWidth="1.5"/>
        <circle cx="140" cy="115" r="8" fill="rgba(206,255,26,0.3)" stroke="#ceff1a" strokeWidth="1.5"/>
        <circle cx="140" cy="115" r="3" fill="#ceff1a"/>
        {/* Code/device icon */}
        <rect x="26" y="50" width="88" height="60" rx="8" fill="#1e1e1e" stroke="rgba(255,255,255,0.12)" strokeWidth="1.2"/>
        <rect x="26" y="50" width="88" height="14" rx="8" fill="#252525"/>
        <rect x="26" y="57" width="88" height="7" fill="#252525"/>
        <circle cx="36" cy="57" r="2.5" fill="#ff5f57"/>
        <circle cx="44" cy="57" r="2.5" fill="#ffbd2e"/>
        <circle cx="52" cy="57" r="2.5" fill="#28c840"/>
        {/* Code lines */}
        <rect x="34" y="72" width="28" height="3.5" rx="1" fill="rgba(206,255,26,0.5)"/>
        <rect x="34" y="79" width="48" height="3" rx="1" fill="rgba(255,255,255,0.15)"/>
        <rect x="34" y="86" width="38" height="3" rx="1" fill="rgba(55,48,163,0.7)"/>
        <rect x="34" y="93" width="56" height="3" rx="1" fill="rgba(255,255,255,0.1)"/>
        {/* Arrow from device to target */}
        <path d="M114 80 Q128 80 132 108" stroke="#ceff1a" strokeWidth="1.5" fill="none" strokeDasharray="4 3"/>
        <polygon points="132,108 126,100 136,100" fill="#ceff1a"/>
        {/* User figure */}
        <circle cx="40" cy="155" r="10" stroke="white" strokeWidth="1.5" fill="none"/>
        <path d="M26 178c0-7.7 6.3-14 14-14s14 6.3 14 14" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
        {/* Checkmark */}
        <circle cx="200" cy="60" r="18" fill="rgba(206,255,26,0.1)" stroke="#ceff1a" strokeWidth="1.5"/>
        <path d="M191 60l6 6 12-12" stroke="#ceff1a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const IllustrationConversion = () => (
    <svg viewBox="0 0 260 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        {/* Contour lines */}
        <ellipse cx="130" cy="165" rx="105" ry="32" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
        <ellipse cx="130" cy="165" rx="80" ry="24" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
        <ellipse cx="130" cy="165" rx="55" ry="16" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
        {/* Funnel */}
        <path d="M55 30 L205 30 L175 80 L85 80 Z" fill="rgba(206,255,26,0.12)" stroke="rgba(206,255,26,0.3)" strokeWidth="1.2"/>
        <path d="M85 80 L175 80 L158 130 L102 130 Z" fill="rgba(206,255,26,0.08)" stroke="rgba(206,255,26,0.2)" strokeWidth="1.2"/>
        <path d="M102 130 L158 130 L148 165 L112 165 Z" fill="rgba(206,255,26,0.15)" stroke="#ceff1a" strokeWidth="1.5"/>
        {/* Funnel labels */}
        <rect x="58" y="46" width="44" height="6" rx="2" fill="rgba(255,255,255,0.2)"/>
        <rect x="94" y="97" width="36" height="5" rx="2" fill="rgba(255,255,255,0.15)"/>
        <text x="130" y="153" textAnchor="middle" fill="#080400" fontSize="10" fontWeight="900" fontFamily="sans-serif">CONVERT</text>
        {/* Rising graph line */}
        <polyline points="30,160 60,140 90,120 120,90 160,60 200,30" stroke="rgba(206,255,26,0.6)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        {/* Graph dots */}
        {[[30,160],[60,140],[90,120],[120,90],[160,60],[200,30]].map(([x,y],i) => (
            <circle key={i} cx={x} cy={y} r="3.5" fill="#ceff1a" opacity={0.4 + i*0.12}/>
        ))}
        {/* Up arrow */}
        <g transform="translate(210, 22)">
            <circle cx="0" cy="0" r="12" fill="rgba(206,255,26,0.15)" stroke="#ceff1a" strokeWidth="1.2"/>
            <path d="M0 6 L0 -4 M-4 -2 L0 -6 L4 -2" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
    </svg>
);

/* ─────────────────────────────────────────
   Service card data
───────────────────────────────────────── */
const services = [
    {
        title: 'User Research & Behaviour Analysis',
        desc: 'We study user behaviour, motivations, and pain points to uncover what drives engagement.',
        icon: (
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="15" cy="15" r="7" stroke="white" strokeWidth="2"/>
                <path d="M21 21l7 7" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M12 15h6M15 12v6" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
        ),
        illustration: <IllustrationResearch />,
    },
    {
        title: 'UX Strategy',
        desc: 'We map user journeys, information architecture, and conversion pathways before screens are designed.',
        icon: (
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <polygon points="18,4 32,12 32,28 18,36 4,28 4,12" stroke="white" strokeWidth="2" fill="none"/>
                <circle cx="18" cy="20" r="4" stroke="white" strokeWidth="2" fill="none"/>
                <line x1="18" y1="4" x2="18" y2="16" stroke="white" strokeWidth="1.5"/>
                <line x1="18" y1="24" x2="18" y2="36" stroke="white" strokeWidth="1.5"/>
            </svg>
        ),
        illustration: <IllustrationStrategy />,
    },
    {
        title: 'UI Design',
        desc: 'Modern, scalable interface systems that balance aesthetics with functionality.',
        icon: (
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <rect x="4" y="7" width="28" height="22" rx="3" stroke="white" strokeWidth="2"/>
                <path d="M4 13h28" stroke="white" strokeWidth="1.8"/>
                <circle cx="9" cy="10" r="1.8" fill="white"/>
                <circle cx="15" cy="10" r="1.8" fill="white"/>
                <rect x="10" y="18" width="16" height="3" rx="1" fill="rgba(255,255,255,0.5)"/>
                <rect x="10" y="24" width="10" height="3" rx="1" fill="rgba(255,255,255,0.3)"/>
            </svg>
        ),
        illustration: <IllustrationUI />,
    },
    {
        title: 'Wireframing & Prototyping',
        desc: 'Ideas tested before development begins — reducing friction and costly revisions.',
        icon: (
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <rect x="5" y="5" width="26" height="26" rx="3" stroke="white" strokeWidth="2" strokeDasharray="5 3"/>
                <path d="M12 12h12M12 18h12M12 24h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        ),
        illustration: <IllustrationWireframe />,
    },
    {
        title: 'Usability Testing',
        desc: 'Real users reveal real problems. We test to understand where users struggle.',
        icon: (
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="13" r="6" stroke="white" strokeWidth="2"/>
                <path d="M8 32c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <path d="M26 6l2.5 2.5-2.5 2.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        illustration: <IllustrationTesting />,
    },
    {
        title: 'Conversion-Focused UX',
        desc: 'We optimise experiences to reduce drop-offs, improve engagement, and increase conversions.',
        icon: (
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <path d="M6 26l7-9 6 5 10-13" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M26 10h5v5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        illustration: <IllustrationConversion />,
    },
];

/* ─────────────────────────────────────────
   Single card
───────────────────────────────────────── */
function ServiceCard({ service }: { service: typeof services[0] }) {
    return (
        <div
            className="relative flex-shrink-0 flex flex-col overflow-hidden"
            style={{
                width: 'clamp(220px, 70vw, 280px)',
                height: 'clamp(380px, 110vw, 480px)',
                borderRadius: 20,
                background: '#1e1e1e',
                border: '1px solid rgba(255,255,255,0.08)',
                padding: 'clamp(16px, 5vw, 28px) clamp(14px, 4vw, 24px) 0',
            }}
        >
            {/* Icon */}
            <div
                style={{
                    width: 'clamp(44px, 12vw, 60px)', height: 'clamp(44px, 12vw, 60px)', borderRadius: 14,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 'clamp(12px, 4vw, 20px)', flexShrink: 0,
                }}
            >
                {service.icon}
            </div>

            {/* Title */}
            <h3
                style={{
                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                    color: '#ffffff',
                    fontSize: 20,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    letterSpacing: '0.01em',
                    marginBottom: 10,
                    flexShrink: 0,
                }}
            >
                {service.title}
            </h3>

            {/* Description */}
            <p
                style={{
                    fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: 13,
                    lineHeight: 1.7,
                    flexShrink: 0,
                }}
            >
                {service.desc}
            </p>

            {/* Illustration — fills bottom */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 200, overflow: 'hidden' }}>
                {service.illustration}
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────
   Infinite scroll strip (CSS keyframe)
───────────────────────────────────────── */
const scrollKeyframes = `
@keyframes ux-scroll {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
}
`;

export default function UXServices() {
    const doubled = [...services, ...services];
    const trackRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const [dragging, setDragging] = useState(false);
    const [paused, setPaused] = useState(false);

    /* ── Mouse drag ── */
    const onMouseDown = useCallback((e: React.MouseEvent) => {
        isDragging.current = true;
        setDragging(true);
        setPaused(true);
        startX.current = e.pageX - (trackRef.current?.offsetLeft ?? 0);
        scrollLeft.current = trackRef.current?.scrollLeft ?? 0;
    }, []);

    const onMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging.current || !trackRef.current) return;
        e.preventDefault();
        const x = e.pageX - (trackRef.current.offsetLeft ?? 0);
        const walk = (x - startX.current) * 1.2;
        trackRef.current.scrollLeft = scrollLeft.current - walk;
    }, []);

    const onMouseUp = useCallback(() => {
        isDragging.current = false;
        setDragging(false);
        setPaused(false);
    }, []);

    /* ── Touch drag ── */
    const onTouchStart = useCallback((e: React.TouchEvent) => {
        isDragging.current = true;
        setPaused(true);
        startX.current = e.touches[0].pageX - (trackRef.current?.offsetLeft ?? 0);
        scrollLeft.current = trackRef.current?.scrollLeft ?? 0;
    }, []);

    const onTouchMove = useCallback((e: React.TouchEvent) => {
        if (!isDragging.current || !trackRef.current) return;
        const x = e.touches[0].pageX - (trackRef.current.offsetLeft ?? 0);
        const walk = (x - startX.current) * 1.2;
        trackRef.current.scrollLeft = scrollLeft.current - walk;
    }, []);

    const onTouchEnd = useCallback(() => {
        isDragging.current = false;
        setPaused(false);
    }, []);

    /* ── Auto-scroll via requestAnimationFrame when not paused ── */
    const animRef = useRef<number | null>(null);
    const autoScrollSpeed = 0.6; // px per frame

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;

        const tick = () => {
            if (!paused && el) {
                el.scrollLeft += autoScrollSpeed;
                // Seamless loop: when we've scrolled past the first copy, reset
                const halfWidth = el.scrollWidth / 2;
                if (el.scrollLeft >= halfWidth) {
                    el.scrollLeft -= halfWidth;
                }
            }
            animRef.current = requestAnimationFrame(tick);
        };
        animRef.current = requestAnimationFrame(tick);
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }, [paused]);

    return (
        <section className="relative overflow-hidden py-10 md:py-24" style={{ background: '#0a0a0a' }}>
            <style>{scrollKeyframes}</style>

            <div className="max-w-[1400px] mx-auto px-4 md:px-16 lg:px-24 mb-8 md:mb-14">
                {/* Heading */}
                <motion.span
                    initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                    className="block text-xs font-bold tracking-[0.22em] uppercase mb-4"
                    style={{ color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                    What We Do
                </motion.span>
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-2xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff' }}>
                        Our UI/UX Services
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-base max-w-sm"
                        style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-manrope), Manrope, sans-serif', lineHeight: 1.7 }}>
                        End-to-end UX services designed to eliminate friction and drive real business growth.
                    </motion.p>
                </div>
            </div>

            {/* ── Draggable infinite scroll strip ── */}
            <div
                ref={trackRef}
                style={{
                    display: 'flex',
                    gap: 'clamp(10px, 3vw, 18px)',
                    paddingLeft: 'clamp(12px, 4vw, 24px)',
                    paddingBottom: 8,
                    overflowX: 'scroll',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    cursor: dragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    WebkitOverflowScrolling: 'touch',
                }}
                className="hide-scrollbar"
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseEnter={() => setPaused(true)}
            >
                {doubled.map((s, i) => (
                    <ServiceCard key={i} service={s} />
                ))}
            </div>

            {/* Fade edges */}
            <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 80, background: 'linear-gradient(90deg, #0a0a0a, transparent)', pointerEvents: 'none', zIndex: 5 }} />
            <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 80, background: 'linear-gradient(-90deg, #0a0a0a, transparent)', pointerEvents: 'none', zIndex: 5 }} />

            <style>{`
                .hide-scrollbar::-webkit-scrollbar { display: none; }
            `}</style>
        </section>
    );
}
